import React, { useState } from 'react'
import { Modal, Button, message } from 'antd'
import styled from 'styled-components'
import { INVITE_RECOVERY, inviteSupportMailto } from './inviteRecoveryCopy'

export const SUPPLIER_INVITE_FOLLOW_UP_MESSAGE =
  "I've invited you to order from us through OMS. Look out for the email from Orders Made Simple and follow the access instructions."

/**
 * Outcome-specific confirmation after invite submit.
 * Never claims inbox delivery — only provider send / fail.
 */
const InviteOutcomeModal = ({
  visible,
  outcome,
  onViewProgress,
  onInviteAnother,
  onResend,
  onClose,
  resending = false,
}) => {
  const [copied, setCopied] = useState(false)

  if (!outcome) return null

  const emailFailed = outcome.emailDispatch === 'failed'
  const emailSent = outcome.emailDispatch === 'sent'
  // Do not use "delivered" — we only know provider accepted the send
  const emailQueued = outcome.emailDispatch === 'queued'
  const supportHref = inviteSupportMailto({
    venueName: outcome.venueName,
    accountNumber: outcome.accountNumber,
    email: outcome.email,
  })

  const title = emailFailed
    ? 'Customer added — email not sent'
    : emailQueued
    ? 'Invitation queued'
    : 'Invitation sent'

  const copyFollowUp = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(SUPPLIER_INVITE_FOLLOW_UP_MESSAGE)
      } else {
        const area = document.createElement('textarea')
        area.value = SUPPLIER_INVITE_FOLLOW_UP_MESSAGE
        document.body.appendChild(area)
        area.select()
        document.execCommand('copy')
        document.body.removeChild(area)
      }
      setCopied(true)
      message.success('Follow-up message copied')
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      message.error('Could not copy message')
      console.error(err)
    }
  }

  return (
    <Modal
      visible={visible}
      title={title}
      onCancel={onClose}
      width='95%'
      style={{ maxWidth: 560 }}
      destroyOnClose
      footer={
        <OutcomeFooter>
          <Button key='copy' onClick={copyFollowUp}>
            {copied ? 'Copied' : 'Copy follow-up message'}
          </Button>
          <Button key='another' onClick={onInviteAnother}>
            Invite another customer
          </Button>
          {emailFailed && onResend ? (
            <Button
              key='resend'
              loading={resending}
              disabled={resending}
              onClick={onResend}
            >
              {resending ? 'Sending…' : 'Resend invitation'}
            </Button>
          ) : null}
          <Button key='progress' type='primary' onClick={onViewProgress}>
            View customer progress
          </Button>
        </OutcomeFooter>
      }
    >
      <Body>
        {emailSent && (
          <>
            <Lead>
              Invitation sent to <strong>{outcome.email}</strong>
            </Lead>
            <p>
              We&apos;ve invited them to access{' '}
              <strong>{outcome.venueName}</strong>. You can follow their
              progress in Customers.
            </p>
            <Note>
              This means OMS handed the email to the email provider — not that
              it is confirmed in their inbox yet.
            </Note>
          </>
        )}

        {emailQueued && (
          <>
            <Lead>
              Invitation queued for <strong>{outcome.email}</strong>
            </Lead>
            <p>
              We&apos;ve set up access to <strong>{outcome.venueName}</strong>.
              The email is queued to send — not delivered yet.
            </p>
          </>
        )}

        {emailFailed && (
          <>
            <Lead>{INVITE_RECOVERY.CUSTOMER_ADDED_EMAIL_FAILED}</Lead>
            <p>
              <strong>{outcome.email}</strong> should have access to{' '}
              <strong>{outcome.venueName}</strong>, but they may not have
              received the invitation email.
            </p>
            <p>
              Resend the invitation (we&apos;ll show the recipient first), or
              copy the follow-up message to send yourself. Copying does not send
              another automatic message from OMS. Recipient changes and
              ownership transfers are not available as recovery shortcuts.
            </p>
            <SupportNote>
              Still stuck? <a href={supportHref}>Contact OMS support</a> (
              {INVITE_RECOVERY.SUPPORT_EMAIL}).
            </SupportNote>
          </>
        )}

        <FollowUpPreview>
          <FollowUpLabel>Suggested follow-up (optional)</FollowUpLabel>
          <FollowUpText>{SUPPLIER_INVITE_FOLLOW_UP_MESSAGE}</FollowUpText>
        </FollowUpPreview>
      </Body>
    </Modal>
  )
}

const Body = styled.div`
  color: #475569;
  font-size: 14px;
  line-height: 1.5;

  p {
    margin: 0 0 10px;
  }
`

const Lead = styled.p`
  margin: 0 0 10px !important;
  color: #0f172a;
  font-size: 15px;
`

const Note = styled.p`
  margin: 0 0 12px !important;
  font-size: 12px;
  color: #64748b;
`

const FollowUpPreview = styled.div`
  margin-top: 8px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
`

const FollowUpLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 6px;
`

const FollowUpText = styled.div`
  font-size: 13px;
  color: #0f172a;
  font-style: italic;
`

const SupportNote = styled.p`
  margin: 0 0 10px !important;
  font-size: 12px;
  color: #64748b;

  a {
    color: #2563eb;
  }
`

const OutcomeFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;

  @media (max-width: 700px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
      margin: 0 !important;
    }
  }
`

export default InviteOutcomeModal
